import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { API_BASE } from '../config';
import { RichTextField, type MentionableEntity } from './RichTextField';
import { dispatchMentionableEntitiesChangedForDocument } from '../utils/mentionable-entities-events';
import { CancelButton } from './ui/cancel-button';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogPrimaryShortcut,
} from './ui/dialog';

export interface CreatedLocation {
  id: string;
  name: string;
  description?: string;
  isManual?: boolean;
}

interface AddLocationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string | undefined;
  projectId?: string;
  initialName?: string;
  mentionableEntities?: MentionableEntity[];
  onCreated?: (location: CreatedLocation) => void;
  onError?: (message: string) => void;
}

function normalizeRichTextDescription(html: string | undefined): string | undefined {
  const s = (html ?? '').trim();
  if (!s) return undefined;
  const emptyTags = ['<p></p>', '<p><br></p>', '<p><br/></p>'];
  if (emptyTags.includes(s)) return undefined;
  return s;
}

export const AddLocationModal: React.FC<AddLocationModalProps> = ({
  open,
  onOpenChange,
  documentId,
  projectId,
  initialName = '',
  mentionableEntities = [],
  onCreated,
  onError,
}) => {
  const { token } = useAuth();
  const { t } = useLocale();
  const [masterKey] = useState(() => new URLSearchParams(window.location.search).get('masterKey'));
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setDescription('');
    setAdding(false);
  }, [open, initialName]);

  const close = (): void => {
    if (adding) return;
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          onOpenChange(true);
          return;
        }
        close();
      }}
    >
      <DialogContent
        className="sm:max-w-[500px]"
        aria-describedby={undefined}
        data-create-entity-from-mention=""
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{t('add_location')}</DialogTitle>
        </DialogHeader>
        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-foreground">
            {t('name')} *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-3 text-base text-foreground"
            placeholder={t('location_name_placeholder')}
            autoFocus
          />
        </div>
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-foreground">
            {t('description')}
          </label>
          <RichTextField
            value={description}
            onChange={setDescription}
            placeholder={t('location_description_placeholder')}
            minHeight="150px"
            maxHeight="40vh"
            mentionableEntities={mentionableEntities}
          />
        </div>
        <DialogFooter className="sm:justify-end gap-2">
          <CancelButton onClick={close} />
          <Button
            dialogPrimary
            className="inline-flex items-center gap-1.5"
            onClick={async () => {
              if (!name.trim() || adding || !documentId) return;
              try {
                setAdding(true);
                const url = masterKey
                  ? `${API_BASE}/api/documents/${documentId}/locations?masterKey=${encodeURIComponent(masterKey)}`
                  : `${API_BASE}/api/documents/${documentId}/locations`;
                const response = await fetch(url, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
                  },
                  body: JSON.stringify({
                    name: name.trim(),
                    description: normalizeRichTextDescription(description) || undefined,
                  }),
                });
                if (!response.ok) {
                  throw new Error('Failed to create location');
                }
                const data = await response.json();
                onCreated?.(data.location);
                void dispatchMentionableEntitiesChangedForDocument(
                  documentId,
                  token ?? null,
                  masterKey,
                  projectId,
                );
                onOpenChange(false);
              } catch (err) {
                console.error('Error creating location:', err);
                onError?.(t('failed_to_create_location'));
              } finally {
                setAdding(false);
              }
            }}
            disabled={!name.trim() || adding}
          >
            {adding ? (
              t('adding')
            ) : (
              <>
                {t('add')}
                <DialogPrimaryShortcut className="ml-0.5" />
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
